import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { SITE } from "@/constants/testIds";

const API = "https://moduleris-api.isvecli.com/api";

const FILTERS = [
  { key: "Tümü", label: "Tümü", slug: "all" },
  { key: "GES", label: "GES Projeleri", slug: "ges" },
  { key: "Enerji/Elektrik", label: "Enerji / Elektrik", slug: "energy" },
  { key: "Mimari", label: "Mimari", slug: "arch" },
  { key: "Restorasyon", label: "Restorasyon", slug: "restore" },
];

const ProjectCard = ({ p, badge }) => (
  <article
    data-testid={SITE.projectCard(p.id)}
    className="project-card relative overflow-hidden border border-neutral-900 bg-[#0a0a0b] group"
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={p.image}
        alt={p.title}
        loading="lazy"
        className="project-img w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute top-3 left-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white bg-black/50 backdrop-blur px-2 py-1 border border-white/10">
        {p.category}
      </div>
      {badge && (
        <div className="absolute top-3 right-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-black bg-[#ff5a00] px-2 py-1 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-black animate-pulse" />
          {badge}
        </div>
      )}
    </div>

    <div className="p-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 font-mono-tech text-[10px] uppercase tracking-[0.28em] text-neutral-500">
        <span>{p.year}</span>
        <span className="w-1 h-1 bg-neutral-700" />
        <span>{p.location}</span>
        {p.power && p.power !== "—" && (
          <>
            <span className="w-1 h-1 bg-neutral-700" />
            <span className="text-[#ff5a00]">{p.power}</span>
          </>
        )}
      </div>
      <h3 className="font-display text-lg md:text-xl font-semibold text-white tracking-tight leading-snug">
        {p.title}
      </h3>
      <p className="text-sm text-neutral-400 mt-3 line-clamp-2">
        {p.description}
      </p>
    </div>

    <div className="project-banner absolute left-0 right-0 bottom-0 bg-[#ff5a00] text-black px-6 py-4 flex items-center justify-between">
      <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] font-semibold">
        Projeyi İncele
      </span>
      <ArrowUpRight size={18} />
    </div>
  </article>
);

export const Portfolio = () => {
  const [active, setActive] = useState("Tümü");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/projects`);
        setProjects(data || []);
      } catch (e) {
        console.error("Failed to load projects", e);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const completed = useMemo(
    () => projects.filter((p) => !p.ongoing),
    [projects]
  );
  const ongoing = useMemo(
    () => projects.filter((p) => p.ongoing),
    [projects]
  );

  const filtered =
    active === "Tümü"
      ? completed
      : completed.filter((p) => p.category === active);

  return (
    <>
      {/* Tamamlanan projeler */}
      <section id="projects" className="py-24 md:py-32 border-b border-neutral-900">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-6 h-px bg-[#ff5a00]" />
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#ff5a00]">
                  Projelerimiz / 04
                </span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                İmzamızı attığımız{" "}
                <span className="italic font-light text-neutral-400">projeler</span>.
              </h2>
              <p className="mt-4 text-neutral-400 text-sm md:text-base max-w-xl">
                İzmir merkezli çalışıyor; Ege bölgesi başta olmak üzere yurt genelinde
                projeler üretiyoruz.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={SITE.filterButton(f.slug)}
                  onClick={() => setActive(f.key)}
                  className={`chip px-4 py-2 text-[11px] uppercase tracking-[0.24em] font-mono-tech ${
                    active === f.key ? "active" : ""
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="aspect-[4/3] bg-neutral-900 animate-pulse border border-neutral-900"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <ProjectCard key={p.id} p={p} />
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 text-neutral-500 font-mono-tech text-sm">
              Bu kategoride henüz tamamlanmış proje bulunmuyor.
            </div>
          )}
        </div>
      </section>

      {/* Devam Eden Projeler */}
      {ongoing.length > 0 && (
        <section
          id="ongoing"
          className="py-24 md:py-32 border-b border-neutral-900 bg-[#050506] relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff5a00]/40 to-transparent" />
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-6 h-px bg-[#ff5a00]" />
                  <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#ff5a00]">
                    Devam Eden Projeler / 05
                  </span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                  Şu an <span className="italic font-light text-neutral-400">aktif</span> sahalar.
                </h2>
                <p className="mt-4 text-neutral-400 text-sm md:text-base max-w-xl">
                  Yıllara yayılan, portföy olarak devam eden ve uzun soluklu projelerimiz.
                </p>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-mono-tech uppercase tracking-[0.28em] text-neutral-400">
                <Loader2 size={14} className="animate-spin text-[#ff5a00]" />
                <span>{ongoing.length} aktif proje</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ongoing.map((p) => (
                <ProjectCard key={p.id} p={p} badge="Devam Ediyor" />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Portfolio;
